import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { qrcodeAPIService } from '~/api/qrcode';
import { qrCodeKeys } from '~/constants/queryKeys';
import { useToastStore } from '~/store/useToastStore';
import { downloadQRs } from '~/utils/download';

export const useGetQrs = (params) => {
  return useQuery({
    queryFn: () => qrcodeAPIService.getListOfQrCodes(params),
    queryKey: [...qrCodeKeys.myQrsData, params],
    select: (res) => res.data,
    refetchOnWindowFocus: false,
    placeholderData: (prevData) => prevData ?? {},
  });
};

export const useDeleteQrs = () => {
  const queryClient = useQueryClient();
  const { setShow } = useToastStore();
  return useMutation({
    mutationFn: (id) => qrcodeAPIService.removeQrCodes({ ids: id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.myQrsData });
    },
    onError: (error) => {
      setShow({
        show: true,
        type: 'error',
        message: `Error Delete QR code: ${error}`,
      });
    },
  });
};

export const useActivateQrs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => qrcodeAPIService.activateQrCodes({ ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.myQrsData });
    },
  });
};

export const useDeactivateQrs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => qrcodeAPIService.deactivateQrCodes({ ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qrCodeKeys.myQrsData });
    },
  });
};

export const useDownloadQr = () => {
  const { setShow } = useToastStore();
  const mimeTypes = {
    pdf: 'application/pdf',
    jpeg: 'image/jpeg',
    png: 'image/png',
    svg: 'image/svg+xml',
  };

  return useMutation({
    mutationFn: async (params: { id: string; format: string; name: string }) => {
      const { id, format, name } = params;
      try {
        const response = await qrcodeAPIService.downloadSingleQrCode({ id, format }, { responseType: 'blob' });

        return downloadQRs({ response, fileName: `${name || 'qr-code'}.${format}`, type: mimeTypes[format] });
      } catch (error) {
        setShow({
          show: true,
          type: 'error',
          message: `Error downloading QR code: ${error}`,
        });
      }
    },
  });
};

export const useDownloadQrs = () => {
  const { setShow } = useToastStore();
  return useMutation({
    mutationFn: async (params: { qr_ids: any; image_format: string }) => {
      try {
        const response = await qrcodeAPIService.downloadGroupOfQrCodes(params, {
          responseType: 'blob',
        });

        return downloadQRs({ response, fileName: 'qr-codes.zip' });
      } catch (error) {
        setShow({
          show: true,
          type: 'error',
          message: `Error downloading QR code: ${error}`,
        });
      }
    },
  });
};
